import { useEffect, useState, type FormEvent } from 'react';
import { Card, StatusChip } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { useResource } from '../lib/hooks';

interface IntegrationStatus {
  connected: boolean;
  hasSecret: boolean;
  lastTestUtc: string | null;
  lastTestOk: boolean | null;
  lastTestMessage: string | null;
  updatedUtc: string | null;
  updatedBy: string | null;
}

interface AutotaskStatus extends IntegrationStatus {
  username: string;
  integrationCode: string;
  zoneName: string | null;
  zoneUrl: string | null;
}

/**
 * Integrations — connecting outside systems. Autotask, the PSA.
 *
 * Under Settings, which is admin-only as a section: this is where an API
 * secret goes in. The API refuses everyone else too (routes/integrations.ts).
 * The secret is write-only: it is encrypted on the server and never sent back,
 * so the box is always empty and leaving it empty keeps the one already saved.
 */
export function IntegrationsPage() {
  return (
    <>
      <PageHead
        title="Integrations"
        lede="Outside systems this app connects to. Credentials are stored encrypted and are never shown again once saved."
      />
      <AutotaskIntegration />
    </>
  );
}

/** The chip every integration card wears, from its saved state and last test. */
function connectionBand(s: IntegrationStatus) {
  const band = !s.connected ? 'neutral' : s.lastTestOk === true ? 'good' : s.lastTestOk === false ? 'critical' : 'warning';
  const label = !s.connected
    ? 'Not connected'
    : s.lastTestOk === true
      ? 'Connected'
      : s.lastTestOk === false
        ? 'Connection failing'
        : 'Saved, not tested';
  return { band, label } as const;
}

/** How the last test went, under the card title. */
function TestResult({ s }: { s: IntegrationStatus }) {
  if (!s.lastTestMessage) return null;
  return (
    <p className="sm mb-4" style={s.lastTestOk ? undefined : { color: 'var(--status-danger)' }}>
      {s.lastTestMessage}
      {s.lastTestUtc && <span className="xs subtle"> · tested {new Date(s.lastTestUtc).toLocaleString()}</span>}
    </p>
  );
}

/** Save / test / disconnect, shared by the cards and their busy state. */
function useIntegrationActions() {
  const [busy, setBusy] = useState<null | 'save' | 'test' | 'disconnect'>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(kind: 'save' | 'test' | 'disconnect', action: () => Promise<unknown>, done: () => void) {
    setBusy(kind);
    setError(null);
    try {
      await action();
      done();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(null);
    }
  }

  return { busy, error, run };
}

function AutotaskIntegration() {
  const status = useResource<AutotaskStatus>('/integrations/autotask');
  const [username, setUsername] = useState('');
  const [integrationCode, setIntegrationCode] = useState('');
  const [secret, setSecret] = useState('');
  const { busy, error, run } = useIntegrationActions();

  useEffect(() => {
    if (!status.data) return;
    setUsername(status.data.username);
    setIntegrationCode(status.data.integrationCode);
  }, [status.data]);

  if (status.loading) return <Loading />;
  if (status.error) return <ErrorBanner message={status.error} />;
  const s = status.data!;
  const { band, label } = connectionBand(s);

  async function save(e: FormEvent) {
    e.preventDefault();
    await run(
      'save',
      async () => {
        await api.put('/integrations/autotask', {
          username: username.trim(),
          integrationCode: integrationCode.trim(),
          ...(secret.trim() ? { secret } : {}),
        });
        setSecret('');
        /* A save is only half the job; prove it straight away. */
        await api.post('/integrations/autotask/test');
      },
      status.reload,
    );
  }

  return (
    <div className="grid grid-2">
      <Card
        title="Autotask"
        subtitle="Connect the Autotask PSA REST API with an API-only user."
        actions={<StatusChip band={band}>{label}</StatusChip>}
      >
        {error && <ErrorBanner message={error} />}
        <TestResult s={s} />

        <form onSubmit={save}>
          <Field label="API username" hint="The API user's username, usually an email-style address.">
            <input
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              required
            />
          </Field>
          <Field
            label="API secret"
            hint={
              s.hasSecret
                ? 'A secret is saved. It is never shown. Leave this empty to keep it, or type a new one to replace it.'
                : 'The secret Autotask generated for the API user.'
            }
          >
            <input
              className="input"
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              autoComplete="new-password"
              placeholder={s.hasSecret ? '•••••••• saved' : ''}
              required={!s.hasSecret}
            />
          </Field>
          <Field label="Integration code" hint="The API tracking identifier from the API user's settings.">
            <input
              className="input"
              value={integrationCode}
              onChange={(e) => setIntegrationCode(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              required
            />
          </Field>

          <div className="row mt-4">
            <button type="submit" className="btn btn-primary" disabled={busy !== null}>
              {busy === 'save' ? 'Saving…' : 'Save and test'}
            </button>
            {s.connected && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy !== null}
                onClick={() => run('test', () => api.post('/integrations/autotask/test'), status.reload)}
              >
                {busy === 'test' ? 'Testing…' : 'Test connection'}
              </button>
            )}
            {(s.connected || s.hasSecret) && (
              <button
                type="button"
                className="btn btn-danger"
                disabled={busy !== null}
                onClick={() => {
                  if (!window.confirm('Disconnect Autotask? The saved credentials will be removed.')) return;
                  void run(
                    'disconnect',
                    async () => {
                      await api.del('/integrations/autotask');
                      setUsername('');
                      setIntegrationCode('');
                      setSecret('');
                    },
                    status.reload,
                  );
                }}
              >
                {busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
              </button>
            )}
          </div>
        </form>

        {s.updatedUtc && (
          <p className="xs subtle mt-4">
            Last changed {new Date(s.updatedUtc).toLocaleString()}
            {s.updatedBy ? ` by ${s.updatedBy}` : ''}
            {s.zoneName ? ` · zone ${s.zoneName}` : ''}
          </p>
        )}
      </Card>

      <Card title="Setting up the API user in Autotask">
        <ol className="sm">
          <li>
            In Autotask go to <strong>Admin › Resources (Users)</strong> and add a new{' '}
            <strong>API User</strong> (the dropdown beside “New”).
          </li>
          <li>
            Give it the <strong>API User (system)</strong> security level, or a copy of it limited to what this app
            should read.
          </li>
          <li>
            On its <strong>Credentials</strong> section, generate the username and secret. Copy the secret now —
            Autotask will not show it again.
          </li>
          <li>
            Under <strong>API Tracking Identifier</strong>, choose <strong>Custom (Internal Integration)</strong>, name
            it “MSP-OPS”, and copy the code it gives you.
          </li>
          <li>Paste the three values here and press Save and test.</li>
        </ol>
        <p className="xs subtle mt-4">
          The test finds your Autotask zone and makes one read-only call (the API usage allowance), so it touches no
          tickets or client data. The secret is stored encrypted and is never sent back to the browser.
        </p>
      </Card>
    </div>
  );
}
